/**
 * The purchase-order PDF's look — colours sampled from the "Order Request"
 * reference — and the web fonts it's drawn with (the shop's: Fraunces for
 * headings, Figtree for text, Hind Siliguri for Bangla).
 */
export const PO_THEME = {
    teal: "#06353d",
    gold: "#ae8849",
    goldText: "#d2ad6c",
    page: "#f7f3ea",
    tableHead: "#e2ebe8",
    band: "#f1ede1",
    row: "#fefdf8",
    rowLine: "#f3f2e6",
    border: "#d9d4c2",
    footerRule: "#d8d2c4",
    ink: "#12302d",
    muted: "#5c6b68",
    cream: "#f7f3ea",
} as const;

export const PO_FONTS = {
    heading: `"Fraunces", Georgia, "Times New Roman", serif`,
    text: `"Figtree", "Hind Siliguri", system-ui, -apple-system, "Segoe UI", sans-serif`,
    bangla: `"Hind Siliguri", "Figtree", system-ui, sans-serif`,
} as const;

const FONTS_URL =
    "https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700" +
    "&family=Fraunces:opsz,wght@9..144,500;9..144,600" +
    "&family=Hind+Siliguri:wght@400;500;600&display=swap";

const FACES: [string, string][] = [
    ["500 24px Fraunces", "Catelyn"],
    ["600 24px Fraunces", "Catelyn"],
    ["400 14px Figtree", "Order"],
    ["500 14px Figtree", "Order"],
    ["600 14px Figtree", "Order"],
    ["700 14px Figtree", "Order"],
    ['400 14px "Hind Siliguri"', "বটতলা রেলগেট"],
];

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

let fontsReady: Promise<void> | null = null;

/**
 * Loads the PDF's fonts once per session. Gives up after a few seconds
 * (e.g. offline) — the PDF is then drawn with the fallback fonts rather
 * than not at all.
 */
export function ensureFonts(): Promise<void> {
    fontsReady ??= (async () => {
        if (!document.querySelector("link[data-po-pdf-fonts]")) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = FONTS_URL;
            link.dataset.poPdfFonts = "1";
            const loaded = new Promise<void>((resolve) => {
                link.onload = () => resolve();
                link.onerror = () => resolve();
            });
            document.head.appendChild(link);
            await Promise.race([loaded, wait(4000)]);
        }
        if (document.fonts?.load) {
            await Promise.race([
                Promise.all(FACES.map(([font, sample]) => document.fonts.load(font, sample).catch(() => []))),
                wait(4000),
            ]);
        }
    })().catch(() => undefined);
    return fontsReady;
}
